import Cookies from "js-cookie";
import {
  createFileRoute,
  Outlet,
  redirect,
  useLoaderData,
} from "@tanstack/react-router";
import { AppSidebar } from "@/features-root-user/components/app-sidebar";
import { rootAdminSidebarData } from "@/features-root-user/components/root-admin-sidebar-data";
import { queryClient } from "@/main";
import {
  fetchUserAttributes,
  signOut,
  fetchMFAPreference,
  FetchUserAttributesOutput,
} from "aws-amplify/auth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { SidebarProvider } from "@/components/ui/sidebar";
import { CommandMenu } from "@/components/command-menu";
import { useEffect } from "react";
import { organizationsQueryOptions } from "@/features-root-user/service/organization";
import { superusersQueryOptions } from "@/features-root-user/service/user";
import { contactsQueryOptions } from "@/features-root-user/service/contact";
import { customerStateWidgetQueryOptions } from "@/features-root-user/service/dashboard";

const isRootUser = (attributes: FetchUserAttributesOutput) => {
  return attributes["custom:rootuser"] === "1";
};

// Define query options for user attributes with longer staleTime
const userAttributesQueryOptions = {
  queryKey: ["userAttributes"],
  queryFn: fetchUserAttributes,
  staleTime: 60 * 1000, // Cache for 1 minute
  cacheTime: 60 * 1000, // Keep in cache for 1 minute too
};

// Define query options for MFA preferences with longer staleTime
const mfaPreferenceQueryOptions = {
  queryKey: ["mfaPreference"],
  queryFn: fetchMFAPreference,
  staleTime: 60 * 1000, // Cache for 1 minute
  cacheTime: 60 * 1000, // Keep in cache for 1 minute too
};

// Define the type for the context
type RootRouteContext = {
  attributes: FetchUserAttributesOutput;
};

export const Route = createFileRoute("/_root")({
  beforeLoad: async () => {
    try {
      // Fetch user attributes with improved caching
      const attributes = await queryClient.fetchQuery(
        userAttributesQueryOptions,
      );

      // Check if the user is a root user
      if (!isRootUser(attributes)) {
        toast.error(
          "You are not authorized to access this page. Please login again.",
        );
        await signOut();
        throw redirect({ to: "/root-user/login" });
      }

      // Fetch MFA preferences with improved caching
      const mfaPreference = await queryClient.fetchQuery(
        mfaPreferenceQueryOptions,
      );

      // Check if TOTP is enabled
      if (!mfaPreference.enabled?.includes("TOTP")) {
        return redirect({
          to: "/root-user/totp-setup",
        });
      }

      // Return fetched data to be added to the route's context
      return { attributes };
    } catch (error) {
      toast.error(
        "Something went wrong while authenticating. Please re-login.",
      );
      await signOut();
      throw redirect({ to: "/root-user/login" });
    }
  },
  loader: async ({ context }: { context: RootRouteContext }) => {
    // Access attributes from context
    const { attributes } = context;
    return { name: attributes.name, email: attributes.email };
  },
  wrapInSuspense: true,
  pendingComponent: () => <LoadingScreen text="Checking authentication..." />,
  component: RouteComponent,
});

function RouteComponent() {
  const defaultOpen = Cookies.get("sidebar:state") !== "false";

  // Access loader data
  const { name, email } = useLoaderData({ from: "/_root" });

  // Prefetch major data endpoints when root layout mounts
  useEffect(() => {
    // This will run only once after the root layout mounts
    const prefetchData = async () => {
      // These prefetches run in parallel and are non-blocking
      queryClient.prefetchQuery(organizationsQueryOptions);
      queryClient.prefetchQuery(superusersQueryOptions);
      queryClient.prefetchQuery(contactsQueryOptions);
      queryClient.prefetchQuery(customerStateWidgetQueryOptions);
    };

    prefetchData();
  }, []);

  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <AppSidebar sidebarData={rootAdminSidebarData({ name, email })} />
      <CommandMenu sidebarData={rootAdminSidebarData({ name, email })} />
      <div
        id="content"
        className={cn(
          "ml-auto w-full max-w-full",
          "peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon)-1rem)]",
          "peer-data-[state=expanded]:w-[calc(100%-var(--sidebar-width))]",
          "transition-[width] duration-200 ease-linear",
          "flex h-svh flex-col",
          "group-data-[scroll-locked=1]/body:h-full",
          "group-data-[scroll-locked=1]/body:has-[main.fixed-main]:h-svh",
        )}
      >
        <Outlet />
      </div>
    </SidebarProvider>
  );
}
